import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Link,
    Checkbox,
} from "@mui/material";
import { styled } from "@mui/system";
import { PageBar } from "../../CommonComponents";
import UploadFiles from "./UploadFiles";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import GoogleSheetsIcon from "@mui/icons-material/Google";

const base = "https://";

const SelectionBox = styled(Box)(({ theme, isSelected }) => ({
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: isSelected ? "#E3F2FD" : "#fff",
    border: `2px solid ${isSelected ? "#1976D2" : "#E0E0E0"}`,
    borderRadius: theme.spacing(1),
    textAlign: "center",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
        backgroundColor: !isSelected ? "#F5F5F5" : undefined,
    },
    marginTop: "2rem",
}));

const offerData = [
    {
        title: "mixmatch",
        subtitle: "VF/SF/Liquidation (single/multi unit)",
        description:
            "Examples: \n₹10 Off per unit (Fixed amount)\n7.5% Off per unit (Percentage)\n7.5% Off on 3 or above units (Percentage)\nBuy 1 Get 1 Free(Least Expensive)",
    },
    {
        title: "Ladder",
        subtitle: "Slab2 offers",
        description:
            "Examples: \nBuy 10 or more → Get 10% Off\nBuy 20 or more → Get 15% Off\nBuy 33 or more → Get 5% Off",
    },
    {
        title: "Combi Offer",
        subtitle: "VF/SF",
        description:
            "Examples: \nBuy Selected Almonds & Cashews, get Selected Kishmish free\nBuy lays chips Combi at Rs 50 Off",
    },
];

const Index = () => {
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState("");

    const [selectedBox, setSelectedBox] = useState(() => {
        const saved = localStorage.getItem("selectedOfferBox");
        return saved ? Number(saved) : 1;
    });

    const handleBoxToggle = (id) => {
        setSelectedBox(id);
        localStorage.setItem("selectedOfferBox", id);
    };

    // APIs ---------------------------------------------------------------

    const apiCall = async (endpoint, payload = {}) => {
        try {
            setLoading(true);
            const response = await axios.post(base + endpoint, payload, {
                headers: {
                    authorization: Cookies.get("ssp_sso_token"),
                    "content-type": "application/json",
                },
            });

            if (response?.data?.status === "success") {
                toast.success("File downloaded successfully", {
                    position: "top-right",
                });
                setLoading(false);
                window.open(response?.data?.body?.url, "_blank");
            } else {
                toast.error(response?.data?.body?.message || "Something went wrong", {
                    position: "top-right",
                });
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
            toast.error("Something went wrong", { position: "top-right" });
        }
    };

    const apiCallCreate = async (endpoint, payload = {}) => {
        try {
            setLoading(true);
            const response = await axios.post(base + endpoint, payload, {
                headers: {
                    authorization: Cookies.get("ssp_sso_token"),
                    "content-type": "application/json",
                },
            });

            if (response?.data?.status === "success") {
                toast.success(response?.data?.body?.message || "Saved Successfully", {
                    position: "top-right",
                });
                setLoading(false);
            } else {
                toast.error(response?.data?.body?.message || "Something went wrong", {
                    position: "top-right",
                });
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
            toast.error("Something went wrong", { position: "top-right" });
        }
    };

    const generateSheet = () => {
        if (selectedBox === 1)
            apiCall(
                "api.more.in/v1/ssp_offers",
                { rawPath: "/generate_mixmatch_gsheet" }
            );
        else if (selectedBox === 2)
            apiCall(
                "api.more.in/v1/ssp_offers",
                { rawPath: "/generate_ladder_sample" }
            );
        else if (selectedBox === 3)
            apiCall(
                "api.more.in/v1/ssp_offers",
                { rawPath: "/generate_combi_sample" }
            );
    };

    const createOffer = async () => {
        if (!url) {
            toast.error("Google sheet url is required", { position: "top-right" });
            return;
        }

        if (selectedBox === 1)
            apiCallCreate(
                "api.more.in/v1/ssp_offers",
                {
                    file_name: url,
                    rawPath: "/create_mixmatch_offer"
                }
            );
        else if (selectedBox === 2)
            apiCallCreate(
                "api.more.in/v1/ssp_offers",
                {
                    file_name: url,
                    rawPath: "/create_ladder_offer"
                }
            );
        else if (selectedBox === 3)
            apiCallCreate(
                "api.more.in/v1/ssp_offers",
                {
                    file_name: url,
                    rawPath: "/create_combi_offer"
                }
            );
    };

    // UI -----------------------------------------------------------------

    return (
        <>
            <Box>
                {loading && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                        }}
                    >
                        <CircularProgress color="primary" size={60} />
                        <Typography variant="h6" sx={{ color: "#fff", mt: 2 }}>
                            Loading...
                        </Typography>
                    </Box>
                )}

                <PageBar text="Offers" />

                {/* SELECTION BOXES */}
                <Box
                    sx={{
                        width: "90%",
                        mx: "auto",
                        display: "flex",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    {offerData.map((offer, index) => (
                        <SelectionBox
                            key={index + 1}
                            isSelected={selectedBox === index + 1}
                            onClick={() => handleBoxToggle(index + 1)}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <Checkbox
                                    checked={selectedBox === index + 1}
                                    onChange={() => handleBoxToggle(index + 1)}
                                    color="primary"
                                />

                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                    {offer.title}
                                </Typography>
                            </Box>

                            <Typography
                                variant="body2"
                                color="black"
                                sx={{ mt: 1, whiteSpace: "pre-line" }}
                            >
                                {offer.subtitle}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="black"
                                sx={{ mt: 1, whiteSpace: "pre-line" }}
                            >
                                {offer.description}
                            </Typography>
                        </SelectionBox>
                    ))}
                </Box>

                {/* GOOGLE SHEET SECTION */}
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        border: "1px solid #E0E0E0",
                        p: 3,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 3,
                        width: "90%",
                        mx: "auto",
                        marginTop: 3,
                    }}
                >
                    <Box sx={{ flex: 1, marginLeft: "30%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <GoogleSheetsIcon sx={{ mr: 1, color: "#34A853" }} />
                            <Typography variant="h6">Google Sheets</Typography>
                        </Box>

                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            * Please make sure you have logged into gmail using moreretail’s
                            official email address.
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Step 1:</strong> Click on the link to generate sample
                            template:
                        </Typography>

                        <Link
                            href="#"
                            underline="always"
                            sx={{ display: "block", mb: 2, color: "#1976D2" }}
                            onClick={generateSheet}
                        >
                            Generate Template
                        </Link>

                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Step 2:</strong> Paste your google sheet URL here:
                        </Typography>

                        <TextField
                            placeholder="Paste your google sheet url here"
                            fullWidth
                            variant="outlined"
                            size="small"
                            onChange={(e) => setUrl(e.target.value)}
                            sx={{
                                mb: 2,
                                width: "50%",
                            }}
                        />

                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Step 3:</strong> Press below button to initiate:
                        </Typography>

                        <Button
                            variant="outlined"
                            startIcon={<GoogleSheetsIcon />}
                            sx={{
                                mt: 1,
                                borderColor: "#E0E0E0",
                                color: "#000",
                                textTransform: "none",
                            }}
                            style={{ backgroundColor: "#E0E0E0", color: "#000" }}
                            onClick={createOffer}
                        >
                            Save Data
                        </Button>
                    </Box>
                </Box>

                <UploadFiles />
            </Box>
        </>
    );
};

export default Index;
